import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, switchMap, tap, throwError } from 'rxjs';
import { AuthApiService } from '../auth/auth-api.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';

export function headerInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const auth = inject(AuthApiService);
  const localStorage = inject(LocalStorageService);
  const isPublicAuthRequest = isUnauthenticatedAuthRequest(request.url);

  const accessToken = isPublicAuthRequest
    ? null
    : request.url.includes('/token')
    ? localStorage.getRefreshToken()
    : localStorage.getAccessToken();

  if (accessToken) {
    request = addAuthorizationHeader(request, accessToken);
  }

  if (shouldAddContentTypeHeader(request)) {
    request = addContentTypeHeader(request);
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      const isUnauthorized = error.status === 401;
      const isTokenOrLoginRequest =
        request.url.includes('/token') ||
        request.url.includes('/login') ||
        isPublicAuthRequest;

      if (isUnauthorized && !isTokenOrLoginRequest) {
        return auth.getNewAccessToken().pipe(
          switchMap((accessToken) => {
            const updatedRequest = addAuthorizationHeader(request, accessToken);
            return next(updatedRequest);
          }),
          catchError(() => {
            auth.logOut(true);
            return throwError(() => error);
          })
        );
      }

      return throwError(() => error);
    })
  );
}

function isUnauthenticatedAuthRequest(url: string): boolean {
  return (
    url.includes('/signup') ||
    url.includes('/verify-email') ||
    url.includes('/resend-verification') ||
    url.includes('/reset-password') ||
    url.includes('/change-password')
  );
}

function addAuthorizationHeader(
  request: HttpRequest<any>,
  accessToken: string
): HttpRequest<any> {
  const currAuthHeader = request.headers.get('Authorization');
  const newAuthHeader = `Bearer ${accessToken}`;

  if (currAuthHeader === newAuthHeader) {
    return request;
  }

  return request.clone({
    setHeaders: {
      Authorization: newAuthHeader,
    },
  });
}

function shouldAddContentTypeHeader<T>(request: HttpRequest<T>): boolean {
  // If body is FormData, do NOT set Content-Type
  if (request.body instanceof FormData) {
    return false; // Keep browser default
  }

  // If body is null or undefined, do NOT set Content-Type
  if (request.body == null) {
    return false;
  }

  switch (request.method) {
    case 'GET':
    case 'OPTIONS':
      return false;
    case 'DELETE':
    case 'POST':
    case 'PUT':
    case 'PATCH':
      return true;
    default:
      throw new Error(`Invalid request method: ${request.method}`);
  }
}

function addContentTypeHeader(request: HttpRequest<any>): HttpRequest<any> {
  if (!request.headers.has('Content-Type')) {
    return request.clone({
      setHeaders: {
        'Content-Type': 'application/json; charset=UTF-8',
      },
    });
  }
  return request;
}
